import { ReactNode } from "react";
import compact from "lodash-es/compact";
import moment from "moment";

// Interfaces
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";

// Helpers
import { numberSuffix } from "@/Common/helpers/number";

// MUI
import { Typography } from "@mui/material";
import { DATE_FORMAT } from "@/Constants/date";

const getDateInformation = (date: Date | undefined, isUTC = false) => {
	if (!date) {
		return {
			month: "",
			day: 0,
		};
	}

	const dateObj = moment(date);
	if (isUTC) {
		dateObj.utc();
	}
	return {
		month: dateObj.format(DATE_FORMAT.monthLong),
		day: Number(String(dateObj)?.substring(8, 10)),
	};
};

const getLastDayTitle = (lastDay: string | number, lastMonth: string, isSameMonth: boolean) => {
	return isSameMonth ? ` - ${lastDay}` : ` - ${lastMonth} ${lastDay}`;
};

function TitleWrapperArea(props: Readonly<{ children: ReactNode }>) {
	const { children } = props;
	return (
		<Typography component={"span"} sx={{ fontSize: "inherit", fontFamily: "inherit", fontWeight: "inherit" }}>
			{children}
		</Typography>
	);
}

function TitleSubscript(props: Readonly<{ title: string; day: number }>) {
	const { title = "", day } = props;
	return (
		<TitleWrapperArea>
			{title}
			<Typography className="superscript" component={"span"}>
				{numberSuffix(day)}
			</Typography>
		</TitleWrapperArea>
	);
}

export default function GetPeriodTitle({
	payPeriod,
	startLabel = "",
	partnerPayPeriod,
}: Readonly<{
	payPeriod?: IPayPeriod;
	startLabel: string;
	partnerPayPeriod?: IPayPeriod | null;
}>) {
	const payPeriodDates = partnerPayPeriod
		? [partnerPayPeriod.startDate, partnerPayPeriod.endDate, payPeriod?.startDate, payPeriod?.endDate]
		: [payPeriod?.startDate, payPeriod?.endDate];

	const sortedDates = compact(payPeriodDates).sort((a, b) => (a > b ? 1 : -1));
	const firstDate = sortedDates.at(0);
	const lastDate = sortedDates.at(-1);

	const { month: firstMonth, day: firstDay } = getDateInformation(firstDate, true);
	const { month: lastMonth, day: lastDay } = getDateInformation(lastDate, true);

	const isSameMonth = firstMonth === lastMonth;
	const isDayPresent = Boolean(firstDay || lastDay);
	const lastDayTitle = getLastDayTitle(lastDay, lastMonth, isSameMonth);

	return (
		<>
			{Boolean(startLabel) && <TitleWrapperArea>{startLabel}</TitleWrapperArea>}
			{isDayPresent && (
				<Typography component={"span"} sx={{ fontSize: "inherit", fontFamily: "inherit", fontWeight: "inherit" }}>
					<span> - </span>
					{Boolean(firstDay) && <TitleSubscript title={`${firstMonth} ${firstDay}`} day={firstDay} />}
					{Boolean(lastDay) && <TitleSubscript title={`${lastDayTitle}`} day={lastDay} />}
				</Typography>
			)}
		</>
	);
}
